import React, { useState } from 'react';
import { Box, Paper, TextField, Button, Typography, Alert, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { changeMyPassword, logout, mustChangePassword } from '../Services/AuthService';
import { extractErrorMessage } from '../Services/httpClient';
import BrandIcon from '../Components/BrandIcon';

const MIN_LENGTH = 8;

/** Devolve a mensagem do primeiro problema do formulário, ou null se está pronto para enviar. */
export const validatePasswordChange = ({ current, next, confirm }) => {
    if (!current) return 'Informe a senha atual.';
    if (next.length < MIN_LENGTH) return `A senha nova deve ter pelo menos ${MIN_LENGTH} caracteres.`;
    if (next === current) return 'A senha nova deve ser diferente da atual.';
    if (next !== confirm) return 'A confirmação não confere com a senha nova.';
    return null;
};

/**
 * Troca da própria senha. Quem acabou de ser criado (ou teve a senha redefinida por um administrador) só chega aqui: o token dele é recusado no
 * resto do backoffice até a troca. Depois de trocar, o token antigo ainda carrega a pendência; por isso sai da sessão e entra de novo.
 */
const ChangePassword = () => {
    const navigate = useNavigate();
    const forced = mustChangePassword();
    const [form, setForm] = useState({ current: '', next: '', confirm: '' });
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        const problem = validatePasswordChange(form);
        if (problem) {
            setError(problem);
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            await changeMyPassword(form.current, form.next);
            logout();
            navigate('/login', { state: { message: 'Senha alterada. Entre com a senha nova.' } });
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        logout();
        navigate('/login');
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff4ec' }}>
            <Paper component="form" onSubmit={handleSubmit} elevation={3} sx={{ p: 4, width: 380, borderRadius: '14px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', mb: 1 }}>
                    <BrandIcon sx={{ fontSize: '2rem' }} />
                    <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: '.15rem', color: '#f77513' }}>
                        re.colocarme
                    </Typography>
                </Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
                    TROCAR SENHA
                </Typography>

                {forced && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Sua senha foi definida por um administrador. Escolha uma senha só sua para continuar.
                    </Alert>
                )}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <TextField label="Senha atual" type="password" fullWidth size="small" value={form.current} onChange={set('current')} sx={{ mb: 2 }} autoFocus />
                <TextField label="Senha nova" type="password" fullWidth size="small" value={form.next} onChange={set('next')} sx={{ mb: 2 }}
                    helperText={`Pelo menos ${MIN_LENGTH} caracteres, com letra maiúscula, minúscula e número.`} />
                <TextField label="Confirme a senha nova" type="password" fullWidth size="small" value={form.confirm} onChange={set('confirm')} sx={{ mb: 3 }} />

                <Button type="submit" fullWidth variant="contained" disabled={isLoading} style={{ backgroundColor: '#f77513', color: 'white' }}
                    startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}>
                    Trocar senha
                </Button>
                <Button fullWidth onClick={handleCancel} sx={{ mt: 1, color: 'text.secondary' }}>
                    Sair
                </Button>
            </Paper>
        </Box>
    );
};

export default ChangePassword;
